/**
 * useScrollAnimation Hook
 * Sistema ligero de animaciones al scroll usando Intersection Observer
 * Respeta prefers-reduced-motion
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Hook para animar elementos cuando entran al viewport
 * @param {Object} options - Opciones del Intersection Observer
 * @param {number} options.threshold - Porcentaje visible para activar (default: 0.1)
 * @param {string} options.rootMargin - Margen del root (default: '0px 0px -100px 0px')
 * @param {boolean} options.triggerOnce - Solo animar una vez (default: true)
 * @returns {Object} { ref, isVisible }
 */
function useScrollAnimation(options = {}) {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -100px 0px',
    triggerOnce = true
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Verificar prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Si el usuario prefiere movimiento reducido, marcar como visible inmediatamente
      setIsVisible(true);
      return;
    }

    const currentRef = ref.current;
    if (!currentRef) return;

    // Ajustar threshold para móvil (menor para que se active más fácilmente)
    const isMobile = window.innerWidth <= 768;
    const effectiveThreshold = isMobile ? Math.max(0.05, threshold * 0.5) : threshold;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Si triggerOnce, dejar de observar después de la primera aparición
          if (triggerOnce && currentRef) {
            observer.unobserve(currentRef);
          }
        } else if (!triggerOnce) {
          // Si no es triggerOnce, permitir que se oculte de nuevo
          setIsVisible(false);
        }
      },
      {
        threshold: effectiveThreshold,
        rootMargin
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

export default useScrollAnimation;
